import {
  type UserJwtResponse,
  v1UsersLoginCreate,
  v1UsersRefreshCreate,
} from '@on3/api';
import type { IUser } from '@on3/ui-lib/api/schema/custom-contracts';
import { editorRoles } from '@on3/ui-lib/contexts/editorRoles';
import { usePrevious } from '@on3/ui-lib/hooks/usePrevious';
import { editorSites } from '@on3/ui-lib/store/editorSites';
import { trackGoogle } from '@on3/ui-lib/utils/tracking';
import jwtDecode from 'jwt-decode';
import { usePathname } from 'next/navigation';
import Router from 'next/router';
import {
  type Context,
  createContext,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Cookies from 'universal-cookie';

import { initialUser } from '../store/mocks/user';
import { externalApi, internalApi, proxyApi } from '../utils/api';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    tp: any;
    dataLayer: any;
  }
}

export const userAccessHeader = (user?: IUser | null) => {
  if (!user) {
    return 'guest';
  }

  const adminAccessRoles = [
    'author',
    'editor',
    'administrator',
    'contributer',
    'developer',
    'rdb1',
    'rdb2',
    'rdb3',
    'rdb4',
    'rdb5',
  ];

  if (
    (Array.isArray(user?.rg) &&
      user?.rg.some((role) => adminAccessRoles.includes(role))) ||
    (!Array.isArray(user?.rg) &&
      user?.rg &&
      adminAccessRoles.includes(user?.rg))
  ) {
    return 'admin';
  } else if (user?.has) {
    return 'subscriber';
  }

  return 'guest';
};

interface IAuthContext {
  user?: IUser;
  login: (username: string, password: string) => Promise<void>;
  setUserToken: (value: UserJwtResponse) => void;
  logout: (redirectUrl?: string) => void;
  setUser: (value: SetStateAction<IUser>) => void;
  isAuthenticated: () => boolean;
  hasRdbAccess: () => boolean;
  isVerified: (playerKey?: number | null) => boolean;
  isVerifiedPlayer: (playerKey?: string | number) => boolean;
  hasRdbLevel5Access: () => boolean;
  hasRdbLevel4Access: () => boolean;
  hasRdbLevel3Access: () => boolean;
  hasRdbLevel2Access: () => boolean;
  hasAccountingAdmin: () => boolean;
  hasPublisherAdmin: () => boolean;
  hasShowsAdmin: () => boolean;
  hasArticleEditAccess: () => boolean;
  canEditArticle: (siteKey: number) => boolean;
  canSlotArticle: () => typeof editorSites | undefined;
  isDeveloper: () => boolean;
  isTester: () => boolean;
  refreshToken: (oldRefreshToken?: string) => Promise<IUser | undefined>;
}

interface IAuthResponse {
  userData?: IUser | null;
  children: React.ReactNode;
}

const AuthContext: Context<IAuthContext> = createContext({} as IAuthContext);
const cookies = new Cookies();

export const AuthProvider = ({ children, userData }: IAuthResponse) => {
  const [user, setUser] = useState<IUser>(userData || initialUser);
  const prevUser = usePrevious(user);
  const pathname = usePathname();

  useEffect(() => {
    async function loadUserFromCookies() {
      const token = cookies.get('token');
      const refresh = cookies.get('refreshToken');

      if (token) {
        if (expiredToken(token) && refresh) {
          refreshToken(refresh);
        } else {
          internalApi.defaults.headers.Authorization = `Bearer ${token}`;
          proxyApi.defaults.headers.Authorization = `Bearer ${token}`;
          externalApi.defaults.headers.Authorization = `Bearer ${token}`;
          const userObj: IUser = jwtDecode(token);

          setUser(userObj);
        }
      }
    }

    if (Router.asPath !== '/logout/') {
      loadUserFromCookies();
    }
  }, []);

  /**
   * Track logins
   */
  useEffect(() => {
    // @note: used for recurly engage
    localStorage?.setItem('user_id', JSON.stringify(user.rac?.[0] || null));

    // @note: used for on3 data layer
    if (user?.uid && prevUser?.uid !== user?.uid) {
      trackGoogle('auth_user', {
        userKey: user.uid,
        userEmail: user.e,
      });
    }
  }, [prevUser?.uid, user]);

  const login = useCallback(
    async (username: string, password: string): Promise<void> => {
      try {
        const data = await v1UsersLoginCreate(externalApi, {
          username,
          password,
        });

        setUserToken(data);

        trackGoogle('login', {
          userKey: data.userId,
          userEmail: data.email,
        });

        return new Promise((resolve) => {
          resolve();
        });
      } catch (error) {
        return new Promise((resolve, reject) => reject(error));
      }
    },
    [],
  );

  const setUserToken = ({
    token,
    refreshBefore,
    refreshToken,
  }: UserJwtResponse) => {
    const tokenExpires = new Date(refreshBefore || 0);
    const week = new Date();

    week.setDate(week.getDate() + 7);

    if (token) {
      // Sets JWT
      cookies.set('token', token, { expires: tokenExpires, path: '/' });
      cookies.set('refreshToken', refreshToken, { expires: week, path: '/' });
      internalApi.defaults.headers.Authorization = `Bearer ${token}`;
      externalApi.defaults.headers.Authorization = `Bearer ${token}`;
      proxyApi.defaults.headers.Authorization = `Bearer ${token}`;

      const userObj = jwtDecode<IUser>(token);

      setUser(userObj);

      return userObj;
    }
  };

  const expiredToken = (token: string) => {
    const expires = new Date(jwtDecode<IUser>(token).exp.date).getTime();
    const now = new Date().getTime();

    // TODO: add a buffer in here? 10 minutes or so
    return expires <= now;
  };

  const isAuthenticated = useCallback(() => {
    const token = cookies.get('token');

    return !expiredToken(token);
  }, []);

  const hasRdbAccess = useCallback(() => {
    if (!user?.rg) {
      return false;
    }

    return user?.rg.some((role) =>
      [
        'rdbadmin',
        'rdb1',
        'rdb2',
        'rdb3',
        'rdb4',
        'rdb5',
        'administrator',
        'developer',
      ].includes(role),
    );
  }, [user]);

  const isTester = useCallback(() => {
    if (!user?.rg) {
      return false;
    }

    return user?.rg.some((role) => ['developer', 'tester'].includes(role));
  }, [user]);

  const isDeveloper = useCallback(() => {
    if (!user?.rg) {
      return false;
    }

    return user?.rg.some((role) => ['developer'].includes(role));
  }, [user]);

  const hasArticleEditAccess = useCallback(() => {
    if (!user?.rg) {
      return false;
    }

    return user?.rg.some((role) =>
      ['editor', 'author', 'administrator'].includes(role),
    );
  }, [user]);

  const canEditArticle = useCallback(
    (siteKey: number) => {
      if (!user?.rg) {
        return false;
      }

      const adminRoles = ['administrator', 'developer', 'desk_editor'];

      if (user.rg.some((role) => adminRoles.includes(role))) {
        return true;
      }

      return user.rg.some((userRole) =>
        editorRoles.some(
          (editorRole) =>
            editorRole.role === userRole && editorRole.site.includes(siteKey),
        ),
      );
    },
    [user],
  );

  const canSlotArticle = useCallback(() => {
    if (!user?.rg) return;

    const allowedSites = editorSites.filter(
      (site) => user.rg?.includes(site.role),
    );

    return allowedSites;
  }, [user]);

  const hasRdbLevel5Access = useCallback(() => {
    if (!user?.rg) {
      return false;
    }

    return user?.rg.some((role) => ['rdb5'].includes(role));
  }, [user]);

  const isVerified = useCallback(
    (playerKey?: number | null) => {
      if (!user || !playerKey) {
        return false;
      }

      return (
        user.pk === String(playerKey) || (user.pks ?? []).includes(playerKey)
      );
    },
    [user],
  );

  const isVerifiedPlayer = useCallback(
    (playerKey?: string | number) => {
      if (!user || !playerKey) {
        return false;
      }

      return user?.pk === playerKey.toString();
    },
    [user],
  );

  const hasRdbLevel4Access = useCallback(() => {
    if (!user?.rg) {
      return false;
    }

    return user?.rg.some((role) =>
      ['rdb5', 'rdb4', 'administrator', 'developer'].includes(role),
    );
  }, [user]);

  const hasRdbLevel3Access = useCallback(() => {
    if (!user?.rg) {
      return false;
    }

    return user?.rg.some((role) =>
      ['rdb5', 'rdb4', 'rdb3', 'administrator', 'developer'].includes(role),
    );
  }, [user]);

  const hasRdbLevel2Access = useCallback(() => {
    if (!user?.rg) {
      return false;
    }

    return user?.rg.some((role) =>
      ['rdb5', 'rdb4', 'rdb3', 'rdb2', 'administrator', 'developer'].includes(
        role,
      ),
    );
  }, [user]);

  const hasAccountingAdmin = useCallback(() => {
    if (!user?.rg) {
      return false;
    }

    return user?.rg.some((role) =>
      ['accounting_admin', 'developer'].includes(role),
    );
  }, [user]);

  const hasPublisherAdmin = useCallback(() => {
    if (!user?.rg) {
      return false;
    }

    return user?.rg.some((role) =>
      ['admin_publisher_dashboard', 'developer'].includes(role),
    );
  }, [user]);

  const hasShowsAdmin = useCallback(() => {
    if (!user?.rg) {
      return false;
    }

    return user?.rg.some((role) =>
      ['shows_admin', 'adminstrator', 'developer'].includes(role),
    );
  }, [user]);

  const logout = useCallback(
    (redirectUrl?: string) => {
      // Clear all cookies at once
      const COOKIES_TO_CLEAR = [
        'token',
        'refreshToken',
        'xf_user',
        'xf_session',
        'xf_csrf',
        'xf_session_admin',
        'xf_dbWriteForced',
      ];

      COOKIES_TO_CLEAR.forEach((cookie) => {
        cookies.remove(cookie);
        document.cookie = `${cookie}=;path=/;expires=Thu, 01 Jan 1970 00:00:01 GMT`;
      });

      setUser(initialUser);

      trackGoogle('logout', {
        userKey: prevUser?.uid,
      });

      if (redirectUrl) {
        Router.push(redirectUrl);
      }
    },
    [prevUser?.uid],
  );

  const refreshToken = useCallback(
    async (oldRefreshToken?: string) => {
      if (!oldRefreshToken) {
        oldRefreshToken = cookies.get('refreshToken');
      }

      let data: UserJwtResponse;

      try {
        data = await v1UsersRefreshCreate(externalApi, {
          refreshToken: oldRefreshToken || '',
        });
      } catch (error) {
        logout();

        return;
      }

      // TODO: move to reusable function
      const { token, refreshBefore, refreshToken: incomingRefreshToken } = data;
      const expires = new Date(refreshBefore);
      const week = new Date();

      week.setDate(week.getDate() + 7);

      if (token) {
        cookies.set('token', token, { expires, path: '/' });
        cookies.set('refreshToken', incomingRefreshToken, {
          expires: week,
          path: '/',
        });
        internalApi.defaults.headers.Authorization = `Bearer ${token}`;
        externalApi.defaults.headers.Authorization = `Bearer ${token}`;
        proxyApi.defaults.headers.Authorization = `Bearer ${token}`;

        // Post message to parent window if on protected embed page
        if (pathname.startsWith('/protected')) {
          window?.top?.postMessage(
            {
              type: 'USER_LOGIN',
              jwt: { token, refreshToken: incomingRefreshToken },
            },
            '*',
          );
        }

        const userObj = jwtDecode<IUser>(token);

        setUser(userObj);

        return userObj;
      }
    },
    [logout, pathname],
  );

  const contextValue = useMemo(
    () => ({
      user,
      login,
      logout,
      setUser,
      isAuthenticated,
      refreshToken,
      setUserToken,
      hasRdbAccess,
      isVerified,
      isVerifiedPlayer,
      hasRdbLevel5Access,
      hasRdbLevel4Access,
      hasRdbLevel3Access,
      hasRdbLevel2Access,
      hasAccountingAdmin,
      hasPublisherAdmin,
      hasShowsAdmin,
      hasArticleEditAccess,
      canEditArticle,
      canSlotArticle,
      isDeveloper,
      isTester,
    }),
    [
      user,
      login,
      logout,
      isAuthenticated,
      refreshToken,
      hasRdbAccess,
      isVerified,
      isVerifiedPlayer,
      hasRdbLevel5Access,
      hasRdbLevel4Access,
      hasRdbLevel3Access,
      hasRdbLevel2Access,
      hasAccountingAdmin,
      hasPublisherAdmin,
      hasShowsAdmin,
      hasArticleEditAccess,
      canEditArticle,
      canSlotArticle,
      isDeveloper,
      isTester,
    ],
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = useContext(AuthContext);

  return context;
};

export { useAuth };
